/**
 * Shared shape definitions for surfaces talking to the backend.
 *
 * Mirrors the `AgentSpec` / `Session` / `Run` records produced by
 * `agent-runtime` + `api-gateway`. Kept loose so schema growth on
 * the backend doesn't crash the UI — UI code only types the bits
 * it actually renders.
 */

export interface AgentRuntime {
  kind: string;
  model?: string;
  extra?: Record<string, unknown>;
}

export interface Agent {
  id: string;
  name: string;
  /** Localized display name. Optional — falls back to `name`. */
  name_zh?: string;
  workspace_id?: string;
  enabled?: boolean;
  system_prompt?: string;
  runtime: AgentRuntime;
}

// ────────────────────────────────────────────────────────────────────
// Settings — Profile / Team / Org
// ────────────────────────────────────────────────────────────────────
//
// Backed by `/v1/me`, `/v1/teams/current`, `/v1/orgs/current`. Field shapes
// mirror the Pydantic schemas in `kos_gateway/api/{me,teams,orgs}.py`.

export interface UserSummary {
  id: string;
  email: string;
  name: string | null;
  /** ISO-8601 UTC timestamp. */
  created_at: string;
}

export interface TenantSummary {
  org_id: string;
  team_id: string;
  team_slug: string;
  team_name: string;
  org_name: string;
  org_plan: string;
  /** Single role string ("team_admin" | "team_editor" | …). */
  role: string;
}

export interface MeResponse {
  user: UserSummary;
  tenant: TenantSummary;
}

export interface TeamResponse {
  id: string;
  org_id: string;
  slug: string;
  name: string;
  member_count: number;
  my_role: string;
  created_at: string;
}

export interface TeamMember {
  user_id: string;
  email: string;
  name: string | null;
  role: string;
  joined_at: string;
}

export interface TeamMembersResponse {
  items: TeamMember[];
}

/** Roles the API will accept on member create / update. Mirrors
 * ``ALLOWED_ROLES`` in ``kos_db/repositories/user_repo.py``. */
export const TEAM_ROLES = ["team_admin", "team_editor", "team_viewer"] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

// ────────────────────────────────────────────────────────────────────
// Multi-team — header switcher + create + invite
// ────────────────────────────────────────────────────────────────────

/** One row per team the caller belongs to. Powers the header switcher and
 *  the "My workspaces" list. */
export interface MyTeamSummary {
  team_id: string;
  team_slug: string;
  team_name: string;
  org_id: string;
  org_name: string;
  org_plan: string;
  role: string;
  is_active: boolean;
}

export interface MyTeamsResponse {
  items: MyTeamSummary[];
}

export interface OrgResponse {
  id: string;
  name: string;
  plan: string;
  created_at: string;
}

// ────────────────────────────────────────────────────────────────────
// Settings — API Keys
// ────────────────────────────────────────────────────────────────────

export interface ApiKeySummary {
  id: string;
  name: string;
  /** First 11 chars of the token (`kos_<7>`). Safe to render in lists. */
  prefix: string;
  created_by_user_id: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

export interface ApiKeyListResponse {
  items: ApiKeySummary[];
}

/** Returned exactly once — `token` will not appear in any subsequent listing. */
export interface ApiKeyCreateResponse {
  id: string;
  name: string;
  prefix: string;
  token: string;
  created_at: string;
  expires_at: string | null;
}

// ────────────────────────────────────────────────────────────────────
// Settings — Usage
// ────────────────────────────────────────────────────────────────────

export interface UsageByModel {
  model: string;
  request_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

export interface UsageSummary {
  period_start: string;
  period_end: string;
  request_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd_total: number;
  by_model: UsageByModel[];
}

// ────────────────────────────────────────────────────────────────────
// LLM models — `/v1/llm/configs` (credentials + vendor model + parameters)
// ────────────────────────────────────────────────────────────────────
//
// One row = one agent-selectable model. Mirrors `LLMConfigSummary` in
// `kos_gateway/api/llm_configs.py`. Secrets are never returned — only
// `has_secret`.

/** Kept in sync with ``ALLOWED_KINDS`` in ``llm_provider_repo.py`` (types only). */
export const LLM_VENDOR_KINDS = [
  "openai",
  "openai-compatible",
  "anthropic",
  "gemini",
  "bedrock",
  "other",
] as const;
export type LlmVendorKind = (typeof LLM_VENDOR_KINDS)[number];

export interface LLMConfigSummary {
  id: string;
  provider_kind: string;
  provider_slug: string | null;
  base_url: string | null;
  has_secret: boolean;
  slug: string;
  display_name: string | null;
  model: string;
  temperature: number;
  max_output_tokens: number | null;
  parameters: Record<string, unknown>;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface LLMConfigListResponse {
  items: LLMConfigSummary[];
}

export interface LLMConfigCreateRequest {
  slug: string;
  kind: string;
  display_name?: string | null;
  base_url?: string | null;
  secret?: string | null;
  model: string;
  temperature?: number;
  max_output_tokens?: number | null;
  parameters?: Record<string, unknown> | null;
}

/** All fields optional; missing == don't touch. */
export interface LLMConfigUpdateRequest {
  kind?: string | null;
  display_name?: string | null;
  base_url?: string | null;
  secret?: string | null;
  model?: string | null;
  temperature?: number | null;
  max_output_tokens?: number | null;
  parameters?: Record<string, unknown> | null;
}

export interface LlmModelTestRequest {
  model?: string | null;
  prompt?: string | null;
}

export interface LlmModelTestResponse {
  ok: boolean;
  latency_ms: number;
  model: string | null;
  sample?: string | null;
  error?: string | null;
}

// ────────────────────────────────────────────────────────────────────
// Knowledge Base — `/v1/kbs/*`
// ────────────────────────────────────────────────────────────────────
//
// Mirrors `KbView` + `FileEntryView` etc. in
// `kos_knowledge_svc/api/{kbs,fs}.py`. The wire schema is intentionally
// thin — anything bigger (e.g. wiki page bodies) lives behind dedicated
// FS routes.

export type KbScope = "personal" | "team";

export interface KbEmbeddingView {
  provider: string;
  model: string;
  dimensions: number;
  batch_size: number;
  base_url: string | null;
  send_dimensions: boolean;
}

export interface KbView {
  id: string;
  scope: KbScope;
  owner_user_id: string | null;
  slug: string;
  name: string;
  description: string | null;
  embedding_config_id: string | null;
  mcp_exposed: boolean;
  mcp_writable: boolean;
  ingestion_config: Record<string, unknown>;
  embedding: KbEmbeddingView;
}

export interface CreateTeamKbRequest {
  name: string;
  slug: string;
  description?: string | null;
  embedding_config_id?: string | null;
  mcp_exposed?: boolean;
  mcp_writable?: boolean;
  ingestion_config?: Record<string, unknown>;
}

export interface UpdateKbRequest {
  name?: string | null;
  description?: string | null;
  ingestion_config?: Record<string, unknown> | null;
  mcp_exposed?: boolean | null;
  mcp_writable?: boolean | null;
}

export type KbFileKind = "file" | "dir";

export interface KbFileEntry {
  name: string;
  path: string;
  kind: KbFileKind;
  size_bytes: number;
  mime_type: string | null;
  content_hash: string | null;
  /** ISO-8601. */
  updated_at: string;
}

export interface KbHeadResponse {
  path: string;
  size_bytes: number;
  body: string;
  truncated: boolean;
  is_text: boolean;
  mime_type: string | null;
}

export interface KbCatBytesResponse {
  path: string;
  size_bytes: number;
  mime_type: string | null;
  presigned_url: string | null;
  /** Base64 — only present when `presign=false` AND we asked for JSON. */
  body_b64?: string | null;
}

export interface KbUploadResponse {
  file: KbFileEntry;
  document_id: string | null;
  /** Arq job id — present when the upload kicked the ingest pipeline,
   *  null when ingestion was skipped (e.g. enqueue failed). */
  ingest_job_id?: string | null;
}

/** Lifecycle stages a `Document` walks through. Open enum so a future
 *  worker can introduce e.g. "reembedding" without a frontend release;
 *  unknown values render as a neutral grey badge. */
export type DocumentStatus =
  | "pending"
  | "ocr"
  | "chunked"
  | "embedded"
  | "indexed"
  | "failed"
  | string;

export interface KbDocumentView {
  id: string;
  kb_id: string;
  title: string | null;
  source_uri: string;
  mime_type: string;
  status: DocumentStatus;
  status_reason: string | null;
  current_version_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface KbDocumentListResponse {
  items: KbDocumentView[];
  limit: number;
  offset: number;
  /** Non-null when there are likely more rows on the next page. */
  next_offset: number | null;
}

export interface KbReembedResponse {
  document_id: string;
  job_id: string | null;
  status: DocumentStatus;
}

export interface KbReextractResponse {
  document_id: string;
  version_id: string;
  job_id: string | null;
}

/* ──────────────────────────────────────────────────────────────────
 * Declared taxonomy — stable, operator-curated catalogue per KB.
 * Mirrors `kos_knowledge_svc.api.taxonomy` view models one-for-one;
 * snake_case is preserved so payloads forward without transform.
 * ────────────────────────────────────────────────────────────────── */

export type TaxonomySource = "system" | "user" | "ai" | string;

export interface KbTopicView {
  id: string;
  kb_id: string;
  slug: string;
  name: string;
  description: string | null;
  parent_ids: string[];
  source: TaxonomySource;
  metadata_json: Record<string, unknown>;
}

export interface KbEntityTypeView {
  id: string;
  kb_id: string;
  slug: string;
  name: string;
  description: string | null;
  source: TaxonomySource;
}

export interface KbEntityView {
  id: string;
  kb_id: string;
  entity_type_id: string;
  canonical_name: string;
  description: string | null;
  source: TaxonomySource;
  metadata_json: Record<string, unknown>;
}

export interface KbAliasView {
  id: string;
  kb_id: string;
  entity_id: string;
  alias: string;
  language: string | null;
  confidence: number;
  is_ambiguous: boolean;
  source: TaxonomySource;
}

export interface KbIdentifierView {
  id: string;
  kb_id: string;
  entity_id: string;
  identifier_type: string;
  identifier_value: string;
  market: string;
  source: TaxonomySource;
}

export interface KbTopicRelationView {
  id: string;
  kb_id: string;
  entity_id: string;
  topic_id: string;
  relation_type: string | null;
  confidence: number | null;
  source: TaxonomySource;
}

export interface KbEntityRelationView {
  id: string;
  kb_id: string;
  source_entity_id: string;
  target_entity_id: string;
  relation_type: string;
  description: string | null;
  confidence: number | null;
  source: TaxonomySource;
}

export interface KbTaxonomyBundle {
  topics: KbTopicView[];
  entity_types: KbEntityTypeView[];
  entities: KbEntityView[];
  aliases: KbAliasView[];
  identifiers: KbIdentifierView[];
  topic_relations: KbTopicRelationView[];
  entity_relations: KbEntityRelationView[];
}

/** Cascade summary returned by `DELETE /taxonomy/topics/{id}`. */
export interface KbTopicCascadeResponse {
  removed: boolean;
  cascaded_topic_relations: number;
  reparented_topics: number;
}

/** Cascade summary returned by `DELETE /taxonomy/entities/{id}`. */
export interface KbEntityCascadeResponse {
  removed: boolean;
  cascaded_aliases: number;
  cascaded_identifiers: number;
  cascaded_topic_relations: number;
  cascaded_entity_relations: number;
}

export interface KbTaxonomyDeleteResponse {
  removed: boolean;
}

/** Must match CHECK on ``kb_entity_relations.relation_type`` (see DB model). */
export const KB_ENTITY_RELATION_TYPES = [
  "tracks_or_leverages",
  "underlying_of",
  "subsidiary_of",
  "parent_of",
  "competitor_of",
  "supplier_of",
  "customer_of",
  "affiliated_with",
  "issued_by",
  "founded_by",
  "employs",
  "other",
] as const;
export type KbEntityRelationType = (typeof KB_ENTITY_RELATION_TYPES)[number];

/** Entity ↔ topic link ``relation_type`` (nullable in API → treat empty as omit). */
export const KB_ENTITY_TOPIC_RELATION_TYPES = ["core_related", "related"] as const;
export type KbEntityTopicRelationType = (typeof KB_ENTITY_TOPIC_RELATION_TYPES)[number];

/** Matches ``kos_db.models.kb_taxonomy.IDENTIFIER_TYPES``. */
export const KB_IDENTIFIER_TYPES = [
  "ticker",
  "isin",
  "cik",
  "wikidata",
  "option",
  "other",
] as const;
export type KbIdentifierKind = (typeof KB_IDENTIFIER_TYPES)[number];

export interface UpsertKbTopicRequest {
  topic_id?: string | null;
  slug: string;
  name: string;
  description?: string | null;
  parent_ids?: string[];
  source?: TaxonomySource;
}

export interface UpsertKbEntityTypeRequest {
  entity_type_id?: string | null;
  slug: string;
  name: string;
  description?: string | null;
  source?: TaxonomySource;
}

export interface UpsertKbEntityRequest {
  entity_id?: string | null;
  canonical_name: string;
  entity_type_id: string;
  description?: string | null;
  source?: TaxonomySource;
}

export interface UpsertKbAliasRequest {
  entity_id: string;
  alias: string;
  language?: string | null;
  confidence?: number;
  is_ambiguous?: boolean;
  source?: TaxonomySource;
}

export interface UpsertKbIdentifierRequest {
  entity_id: string;
  identifier_type: string;
  identifier_value: string;
  market?: string | null;
  source?: TaxonomySource;
}

export interface UpsertKbTopicRelationRequest {
  entity_id: string;
  topic_id: string;
  relation_type?: string | null;
  confidence?: number | null;
  source?: TaxonomySource;
}

export interface UpsertKbEntityRelationRequest {
  source_entity_id: string;
  target_entity_id: string;
  relation_type: string;
  description?: string | null;
  confidence?: number | null;
  source?: TaxonomySource;
}

/* ──────────────────────────────────────────────────────────────────
 * LLM extraction — runs, candidates, derived facts, prompt preview.
 * Mirrors `kos_knowledge_svc.api.extraction`. `value_numeric` lands as
 * a string because `Decimal` precision matters on percentages /
 * currency; consumers parse on display.
 * ────────────────────────────────────────────────────────────────── */

export const EXTRACTORS = ["topic", "claim_fact", "relation"] as const;
export type ExtractorKind = (typeof EXTRACTORS)[number];

export const EXTRACTION_RUN_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
] as const;
export type ExtractionRunStatus = (typeof EXTRACTION_RUN_STATUSES)[number] | string;

export const CANDIDATE_STATUSES = ["pending", "promoted", "rejected"] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

export const CANDIDATE_KINDS = ["topics", "predicates", "entities"] as const;
export type CandidateKind = (typeof CANDIDATE_KINDS)[number];

export interface KbPredicateView {
  id: string;
  kb_id: string;
  slug: string;
  name: string;
  description: string | null;
  subject_types: string[];
  object_types: string[];
  source: TaxonomySource;
}

export interface KbExtractionRunView {
  id: string;
  kb_id: string;
  document_id: string;
  version_id: string;
  extractor: ExtractorKind | string;
  extractor_version: string;
  status: ExtractionRunStatus;
  /** ISO-8601, set once the worker picks the row up. */
  started_at: string | null;
  /** ISO-8601, set on a terminal status. */
  finished_at: string | null;
  chunks_processed: number;
  chunks_failed: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  error_message: string | null;
  model: string | null;
  provider: string | null;
}

export interface KbTopicCandidateView {
  id: string;
  kb_id: string;
  proposed_label: string;
  proposed_description: string | null;
  proposed_keywords: string[];
  confidence: number;
  occurrences: number;
  status: CandidateStatus;
  rejection_reason: string | null;
  promoted_topic_id: string | null;
  evidence_chunk_id: string | null;
  evidence_text: string | null;
  source: TaxonomySource;
}

export interface KbPredicateCandidateView {
  id: string;
  kb_id: string;
  proposed_label: string;
  proposed_description: string | null;
  example_subject_types: string[];
  example_object_types: string[];
  confidence: number;
  occurrences: number;
  status: CandidateStatus;
  rejection_reason: string | null;
  promoted_predicate_id: string | null;
  source: TaxonomySource;
}

export interface KbEntityCandidateView {
  id: string;
  kb_id: string;
  proposed_canonical_name: string;
  proposed_aliases: string[];
  entity_type_id: string | null;
  identifier_type: string | null;
  identifier_value: string | null;
  market: string | null;
  confidence: number;
  occurrences: number;
  status: CandidateStatus;
  rejection_reason: string | null;
  promoted_entity_id: string | null;
  evidence_chunk_id: string | null;
  evidence_text: string | null;
  source: TaxonomySource;
}

export interface KbMetricFactView {
  id: string;
  kb_id: string;
  subject_entity_id: string | null;
  subject_text: string;
  metric_slug: string;
  metric_label: string;
  /** Inclusive ISO date. */
  period_start: string | null;
  /** Inclusive ISO date. */
  period_end: string | null;
  /** Free-form period label (e.g. "FY2024"). */
  period_text: string | null;
  /** Decimal as string — parse with `Number(…)` only for display, never for math. */
  value_numeric: string;
  unit: string | null;
  currency: string | null;
  evidence_chunk_id: string;
  evidence_text: string;
  confidence: number;
  derived_from_mention_id: string | null;
  source: TaxonomySource;
}

export interface PromoteTopicRequest {
  slug: string;
}

export interface PromotePredicateRequest {
  slug: string;
}

export interface PromoteEntityRequest {
  entity_type_id: string;
}

export interface RejectCandidateRequest {
  rejection_reason?: string | null;
}

/** Prompt preview — pure prompt-builder over a synthetic chunk; no
 *  LLM call is made and no run is recorded. */
export interface KbPreviewRequest {
  extractor: ExtractorKind;
  chunk_id?: string;
  seq?: number;
  content: string;
  metric_hints?: string[];
}

export interface KbPreviewResponse {
  extractor: ExtractorKind;
  system: string;
  user: string;
}

export interface KbSearchRequest {
  query: string;
  /** 1–50; defaults to the server's KOS_KB_SEARCH_TOP_K (8). */
  top_k?: number | null;
  /** 0.0–1.0 cosine similarity floor; null for "no floor". */
  score_threshold?: number | null;
  document_ids?: string[] | null;
}

export interface KbSearchHit {
  chunk_id: string;
  document_id: string;
  version_id: string;
  seq: number;
  /** Single-line snippet, server-truncated to KOS_KB_SNIPPET_CHARS. */
  snippet: string;
  /** Cosine similarity in [0, 1]; higher is better. */
  score: number;
  document_title: string | null;
  source_uri: string;
  embedding_model: string;
  token_count: number;
}

export interface KbSearchResponse {
  hits: KbSearchHit[];
  query: string;
  top_k: number;
}

// ────────────────────────────────────────────────────────────────────
// Agents — `/v1/agents/*`
// ────────────────────────────────────────────────────────────────────
//
// Mirrors the Pydantic schemas in `kos_gateway/api/agents.py` +
// `kos_common.schemas.AgentSpec`. The runtime treats unknown fields
// in `spec` as no-ops, so the UI types only the bits we actually
// edit; everything extra round-trips through `Record<string, unknown>`.

export const AGENT_RUNTIMES = [
  "openai-agents",
  "claude-agent",
  "langchain-deepagents",
  "codex",
] as const;
export type AgentRuntimeName = (typeof AGENT_RUNTIMES)[number];

/**
 * Legacy literal kinds. v0 hard-coded these on `AgentModelConfig.provider`;
 * v1 carries an arbitrary slug from `/v1/llm/configs` instead. The union
 * is kept around because:
 *   - the agent form still falls back to it when the team has no providers
 *     registered yet (offering at least the vendor defaults), and
 *   - existing seed agents that predate the registry use these literals.
 */
export const MODEL_PROVIDERS = ["openai", "anthropic", "openai-compatible"] as const;
export type AgentModelProvider = (typeof MODEL_PROVIDERS)[number];

export interface AgentModelConfig {
  /** Slug of the bound `llm_providers` row, OR a legacy literal kind. */
  provider: string;
  /** Resolved provider kind (set by the gateway). Used by the runtime to
   *  pick the correct adapter without a second DB lookup. */
  provider_kind?: string | null;
  model: string;
  temperature: number;
  max_output_tokens: number | null;
  base_url: string | null;
}

/** Forward-compatible policy axes for tool calls. Defaults match the
 *  backend's ``ToolRef`` permissive defaults so any spec read from before
 *  these fields existed still round-trips cleanly. The approvals layer
 *  (see ``docs/adr/draft-tool-runtime-and-approvals.md``) reads ``policy``
 *  to decide whether to gate the call on a human decision. */
export type ToolRiskTier = "safe" | "risky" | "dangerous";
export type ToolPolicy = "allow" | "require_approval" | "deny";

export interface AgentToolRef {
  name: string;
  kind: "builtin" | "http" | "mcp";
  config: Record<string, unknown>;
  /** Default ``"safe"``. Optional in JSON for legacy specs. */
  risk_tier?: ToolRiskTier;
  /** Default ``"allow"``. Optional in JSON for legacy specs. */
  policy?: ToolPolicy;
}

/** Names the runtime knows how to wire as builtin tools.
 *
 * Mirror of ``_KNOWN_BUILTIN_TOOLS`` in
 * ``backend/services/api-gateway/src/kos_gateway/api/agents.py`` (and the
 * registry under ``backend/services/agent-runtime/src/kos_runtime/tools/``).
 * Kept as a const tuple so the agent-drawer Spec editor can populate a
 * dropdown without round-tripping the API on every render.
 *
 * If you add a new builtin server-side, append it here. The gateway will
 * still 422 on unknown names so a stale frontend can't silently corrupt
 * a spec — this list is purely for UX. */
export const KNOWN_BUILTIN_TOOLS = ["echo"] as const;
export type KnownBuiltinTool = (typeof KNOWN_BUILTIN_TOOLS)[number];

export const TOOL_RISK_TIERS: readonly ToolRiskTier[] = ["safe", "risky", "dangerous"] as const;
export const TOOL_POLICIES: readonly ToolPolicy[] = ["allow", "require_approval", "deny"] as const;

export interface AgentMcpServerRef {
  id: string;
  name: string;
}

export interface AgentMemorySpec {
  short_term_token_budget: number;
  summarize_every_turns: number | null;
  episodic_recall: boolean;
}

export interface AgentGuardrailRef {
  name: string;
  config: Record<string, unknown>;
}

/**
 * Agent ↔ Skill binding. The skill itself lives in the team's
 * ``installed_skills`` table; this is just the per-agent reference.
 *
 * `version` pins the catalog entry the agent expects; null means
 * "always use the live catalog version" — matches the server-side
 * `SkillRef` shape in `kos_common.schemas.agent_spec`.
 */
export interface AgentSkillRef {
  id: string;
  version: string | null;
}

export interface AgentSpec {
  runtime: AgentRuntimeName;
  /** Preferred: bind to a named LLM preset row (`llm_configs`). When set,
   *  the api-gateway resolves it at write time and back-fills the
   *  corresponding fields onto `model`, so the runtime keeps reading a
   *  single shape. `null` means legacy spec — `model` fields are used
   *  directly. See AgentSpec.llm_config_id in
   *  `kos_common.schemas.agent_spec`. */
  llm_config_id: string | null;
  model: AgentModelConfig;
  system_prompt: string;
  tools: AgentToolRef[];
  mcp_servers: AgentMcpServerRef[];
  knowledge_base_ids: string[];
  skills: AgentSkillRef[];
  memory: AgentMemorySpec;
  guardrails: AgentGuardrailRef[];
  metadata: Record<string, unknown>;
}

export interface AgentSummary {
  id: string;
  org_id: string;
  team_id: string;
  name: string;
  description: string | null;
  created_by_user_id: string;
  current_version_id: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  spec: AgentSpec | null;
}

export interface AgentListResponse {
  items: AgentSummary[];
  next_offset: number | null;
}

export interface AgentVersion {
  id: string;
  agent_id: string;
  version: number;
  spec: Record<string, unknown>;
  created_by_user_id: string;
  created_at: string;
}

export interface CreateAgentRequest {
  name: string;
  description?: string | null;
  spec?: Partial<AgentSpec>;
}

export interface UpdateAgentRequest {
  name?: string | null;
  description?: string | null;
  spec?: Partial<AgentSpec> | null;
}

// ────────────────────────────────────────────────────────────────────
// Activity — `/v1/activity/*`
// ────────────────────────────────────────────────────────────────────
//
// Mirror of `agent-runtime`'s `/v1/agent_sessions/*` schemas, exposed
// through the gateway's `/v1/activity/*` proxy. The runtime returns
// `{object:"list", data:[...]}` envelopes — we keep that wire shape.

export interface ActivitySession {
  id: string;
  agent_id: string;
  external_thread_key: string | null;
  title: string | null;
  runtime: string | null;
  status: string | null;
  last_response_id: string | null;
  runtime_session_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface ActivityRequest {
  id: string;
  response_id: string | null;
  previous_response_id: string | null;
  status: string | null;
  outcome: string | null;
  started_at: string | null;
  ended_at: string | null;
  latency_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  model: string | null;
  provider: string | null;
  runtime: string | null;
}

export interface ActivitySessionDetail extends ActivitySession {
  requests?: ActivityRequest[];
}

export interface ActivitySpan {
  id: string;
  request_id: string;
  parent_span_id: string | null;
  sdk_span_id: string | null;
  kind: string | null;
  name: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  status: string | null;
  attributes: Record<string, unknown>;
  events: unknown[];
  error: Record<string, unknown> | null;
  ab_variant: string | null;
}

export interface ActivityListEnvelope<T> {
  object: "list";
  data: T[];
}

/** One row in the rebuilt session conversation. Mirrors `MessageRecord`
 *  on the runtime: `role` is "user" or "assistant", `content` is the
 *  prompt text or the assistant's `final_text`, and `response_id`
 *  pins assistant rows back to the trace. */
export interface ActivityMessage {
  role: "user" | "assistant";
  content: string;
  response_id: string | null;
}

// ────────────────────────────────────────────────────────────────────
// Channels — `/v1/channels/*`
// ────────────────────────────────────────────────────────────────────
//
// Mirrors `AdapterDescriptorResponse` + `BindingSummary` in
// `kos_gateway/api/channels.py`. The wire schema NEVER carries
// credentials — only `has_credentials: boolean`. Mutation operations
// require `team_admin`.

/** Lifecycle stage from `kos_channels.AdapterStatus`. The frontend uses
 *  this to decide whether to render the "Configure" button or a "coming
 *  soon" pill. */
export type AdapterStatus = "available" | "experimental" | "planned";

export interface AdapterDescriptor {
  kind: string;
  display_name: string;
  description: string;
  status: AdapterStatus;
  /** Pydantic-derived JSON schema for the per-binding `config` payload.
   *  `null` for adapters that take no config (the bare API binding). */
  config_schema: Record<string, unknown> | null;
  needs_credentials: boolean;
  docs_url: string | null;
}

export interface AdapterListResponse {
  items: AdapterDescriptor[];
}

export interface ChannelBindingSummary {
  id: string;
  kind: string;
  slug: string;
  display_name: string | null;
  agent_id: string;
  /** Echoed back as parsed JSON. Validated against the adapter's
   *  `config_schema` on POST/PATCH. */
  config: Record<string, unknown>;
  has_credentials: boolean;
  enabled: boolean;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ChannelBindingListResponse {
  items: ChannelBindingSummary[];
}

export interface CreateChannelBindingRequest {
  kind: string;
  slug: string;
  display_name?: string | null;
  agent_id: string;
  config?: Record<string, unknown>;
  /** Encrypted at rest with versioned Fernet. Omit for adapters that
   *  don't take secrets (the public-API binding). */
  credentials?: Record<string, unknown> | null;
  enabled?: boolean;
}

export interface UpdateChannelBindingRequest {
  display_name?: string | null;
  config?: Record<string, unknown> | null;
  /** Missing → keep existing; object → rotate; `null` → drop the stored secret. */
  credentials?: Record<string, unknown> | null;
  enabled?: boolean | null;
}

// ────────────────────────────────────────────────────────────────────
// External MCP servers — `/v1/mcp/external/*`
// ────────────────────────────────────────────────────────────────────
//
// Mirrors `ExternalMcpSummary` in `kos_gateway/api/mcp_external.py`.
// The wire schema NEVER carries the auth-header blob — only
// `has_credentials: boolean`. Mutations require `team_admin`.

/** Transports the runtime can actually call out to. `stdio` is local-only
 *  and intentionally out of scope for a remote registration. */
export type ExternalMcpTransport = "http" | "sse";

export interface ExternalMcpSummary {
  id: string;
  slug: string;
  display_name: string;
  description: string | null;
  /** Stored as free-form text; only `"http"` and `"sse"` are accepted by
   *  the API today, but new transports may land without a wire change. */
  transport: string;
  url: string;
  has_credentials: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExternalMcpListResponse {
  items: ExternalMcpSummary[];
}

export interface CreateExternalMcpRequest {
  slug: string;
  display_name: string;
  description?: string | null;
  transport?: ExternalMcpTransport;
  url: string;
  /** JSON dict of header → value. Encrypted at rest with versioned Fernet.
   *  Omit (or pass `null`) for open / public MCP servers. */
  auth_headers?: Record<string, string> | null;
  enabled?: boolean;
}

export interface UpdateExternalMcpRequest {
  display_name?: string;
  description?: string | null;
  transport?: ExternalMcpTransport;
  url?: string;
  /** Missing → keep existing; object → rotate; `null` → drop the stored secret. */
  auth_headers?: Record<string, string> | null;
  enabled?: boolean;
}

// ────────────────────────────────────────────────────────────────────
// Skills catalog — `/v1/skills/*`
// ────────────────────────────────────────────────────────────────────
//
// Mirrors `SkillSpecResponse` in `kos_gateway/api/skills.py`. The catalog
// is read-only today; per-team install state lands in a follow-up phase
// and will reuse the same `SkillSpec` shape for the items.

/** Coarse grouping for the catalog filter chips. The backend ships a
 *  fixed enum (`kos_eval.skills.SkillCategory`) but we accept `string`
 *  on the wire so adding categories doesn't require a frontend release. */
export type SkillCategory = string;

/** UX-only hint surfaced as a badge on the catalog card. Real
 *  enforcement happens at the tool layer. */
export type SkillRisk = "safe" | "caution" | "destructive";

export interface SkillSpec {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: SkillCategory;
  tools: string[];
  risk: SkillRisk;
  version: string;
  builtin: boolean;
}

export interface SkillCatalogResponse {
  items: SkillSpec[];
  /** Stable category list — drives the filter bar order so it doesn't
   *  flicker between requests. */
  categories: SkillCategory[];
}

export interface InstalledSkillsResponse {
  items: SkillSpec[];
}

/** Body for `POST /v1/skills/install`. The backend looks the spec up
 *  by id server-side, so the wire payload is intentionally minimal —
 *  no version/description/etc. that a stale client might forge. */
export interface InstallSkillRequest {
  skill_id: string;
}

// ────────────────────────────────────────────────────────────────────
// Marketplace — `/v1/marketplace/*` (frontend-only mock today)
// ────────────────────────────────────────────────────────────────────
//
// Surfaced via the `/marketplace` route. Positioned as the platform's
// curated catalog of **data packs** (pre-built KBs) and **agent
// templates** (ready-made agent configs). Skills and MCP plugins are
// NOT listed here — they have their own dedicated surfaces (`/skills`,
// `/mcp`). See `docs/adr/0001-marketplace-scope.md` for the rationale.
//
// Backend endpoints (`/v1/marketplace/catalog`, `/v1/marketplace/install`,
// `/v1/marketplace/installed`) are NOT YET shipped. The frontend hides
// the gap behind a mock catalog + an in-memory `installed` set; the
// shapes below mirror what the real API will return so the swap is a
// one-file change in `features/marketplace/marketplace-catalog-mock.ts`.

/** Top-level marketplace tab. One value per tab in the UI. Backend
 *  enum will mirror this exactly. */
export type MarketplaceKind = "datapack" | "agents";

/** UX hint badge — same vocabulary as `SkillRisk` so a marketplace
 *  item that ships a tool can reuse the existing badge component. */
export type MarketplaceRisk = "safe" | "caution" | "destructive";

export interface MarketplaceItem {
  id: string;
  /** Tab this item belongs to. The browser routes the install handler
   *  off this field. */
  kind: MarketplaceKind;
  name: string;
  /** One-line marketing copy shown next to the icon. */
  tagline: string;
  /** Long-form description for the card body. */
  description: string;
  /** Coarse grouping for the future filter row. `string` to keep new
   *  backend categories from forcing a frontend release. */
  category: string;
  /** Bullet list of what's bundled — e.g. ["白酒板块 38 只股票", "近 5 年日线",
   *  "财报基本面数据"]. Rendered as small chips. */
  bundles: string[];
  /** UX hint: how invasive the install is. `safe` means it only mounts
   *  a read-only KB; `caution` flags write access; `destructive` is
   *  reserved for items that mutate tenant state. */
  risk: MarketplaceRisk;
  /** Catalog version. Bumped server-side when the bundled data refreshes. */
  version: string;
  /** Optional vendor label — "knode-os" for first-party items, partner
   *  name otherwise. Surfaced as a secondary tag on the card. */
  vendor?: string;
  /** For `kind === "datapack"`: the slug of the KB that the install
   *  action provisions / binds. The frontend only displays it; the
   *  backend will own the actual KB lifecycle. */
  kb_slug?: string;
}

export interface MarketplaceCatalogResponse {
  items: MarketplaceItem[];
}

export interface InstalledMarketplaceResponse {
  /** Item ids the active team has installed. Matched against
   *  `MarketplaceItem.id` to flip the card's button to "Uninstall". */
  installed_ids: string[];
}

/** Body for `POST /v1/marketplace/install`. */
export interface InstallMarketplaceRequest {
  item_id: string;
}

// ────────────────────────────────────────────────────────────────────
// Approvals — `/v1/approvals/*`
// ────────────────────────────────────────────────────────────────────
//
// Mirrors the Pydantic schemas in
// `kos_common.schemas.approvals` + the ORM `Approval` row in
// `kos_db.models.runtime.approval`. L1a backend ships read + resolve;
// the L2 runtime hook is what fills the table. See
// `docs/adr/draft-tool-runtime-and-approvals.md` for the full plan.

export const APPROVAL_STATUSES = ["pending", "approved", "denied", "timeout", "cancelled"] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

/** What an operator can submit. Other terminal states (`timeout`,
 *  `cancelled`) are produced by the system, not by a click. */
export type ApprovalDecision = "approve" | "deny";

/** Mirror of `ToolRef.kind` the runtime accepts for an approval. `http`
 *  is rejected by the L0 gateway validator on writes, so we don't list
 *  it here. */
export type ApprovalToolKind = "builtin" | "mcp";

export interface ApprovalSummary {
  id: string;
  org_id: string;
  team_id: string;
  request_id: string;
  session_id: string;
  agent_id: string;

  tool_call_id: string;
  tool_name: string;
  tool_kind: ApprovalToolKind;
  /** Verbatim arguments the model proposed. Surfaced in the operator
   *  drawer so they can see exactly what's being approved. */
  tool_arguments: Record<string, unknown>;
  risk_tier: ToolRiskTier;
  reason: string | null;
  /** Snapshot at create time. Honoured by the API even if the agent
   *  spec is later edited to widen who can approve. */
  required_role: string;

  requested_by_user_id: string;
  /** Best-effort `users.name` for `requested_by_user_id`, populated by
   *  the gateway on read paths. `null` for hard-deleted users; the UI
   *  falls back to a hex prefix. */
  requested_by_display_name: string | null;
  /** Short label like `"feishu:ou_xxx"` derived from `channel_users`,
   *  or `null` when the user only ever used the web/API surface. */
  requested_via: string | null;
  /** ISO-8601. The L2 worker will flip this row to `timeout` past
   *  this point; the API returns the row regardless. */
  expires_at: string;

  status: ApprovalStatus;
  decided_by_user_id: string | null;
  decided_by_display_name: string | null;
  decided_via: string | null;
  decided_at: string | null;
  note: string | null;

  created_at: string;
  updated_at: string;
}

export interface ApprovalListResponse {
  items: ApprovalSummary[];
  total: number;
  /** Next offset to pass to keep paging; `null` when at end. */
  next_offset: number | null;
}

export interface ApprovalResolveRequest {
  decision: ApprovalDecision;
  /** ≤ 256 chars. Persisted on the row for audit + the eval pipeline. */
  note?: string | null;
}

export interface ApprovalResolveAllRequest {
  decision: ApprovalDecision;
  note?: string | null;
}

/** Wire shape of `POST /v1/approvals/run/{request_id}/resolve-all`. */
export interface ApprovalResolveAllResponse {
  count: number;
}

export const DEFAULT_AGENT_SPEC: AgentSpec = {
  runtime: "openai-agents",
  llm_config_id: null,
  model: {
    provider: "openai",
    model: "gpt-4o-mini",
    temperature: 0.7,
    max_output_tokens: null,
    base_url: null,
  },
  system_prompt: "",
  tools: [],
  mcp_servers: [],
  knowledge_base_ids: [],
  skills: [],
  memory: {
    short_term_token_budget: 4000,
    summarize_every_turns: 12,
    episodic_recall: false,
  },
  guardrails: [],
  metadata: {},
};
